import { LeadSequenceReturnReadable, leadsSequencesListOptions } from '@crm/api'
import { CampaignStatus, CampaignSteps } from '@crm/components'
import { TableActions, TableContainer } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, MenuProps, TableColumnsType } from 'antd'
import { Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CampaignFilter } from './CampaignFilter'
import { CampaignHeader } from './CampaignHeader'
import { DeleteCampaign } from './DeleteCampaign'
import { ActionsType } from './types'

type ModalType = 'delete'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  delete: DeleteCampaign
}

export const CampaignsCol = () => {
  const { t } = useLocales()
  const { id } = useParams()
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    sequence: LeadSequenceReturnReadable | null
  }>({ sequence: null, type: null })

  const { params } = useQueryUpdate()

  const { data, isLoading } = useQuery({
    ...leadsSequencesListOptions({
      path: { lead_id: String(id) },
      query: params
    }),
    enabled: !!id
  })

  const actionItems = (
    sequence: LeadSequenceReturnReadable
  ): MenuProps['items'] => [
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ sequence, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<LeadSequenceReturnReadable> = [
    {
      title: t('generic.name'),
      dataIndex: ['sequence', 'title'],
      key: 'sequence_title'
    },
    {
      title: t('campaigns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 250,
      render: (_, sequence) =>
        id ? <CampaignStatus sequence={sequence} leadId={id} /> : '-'
    },
    {
      title: t('campaigns.step'),
      dataIndex: ['current_step', 'id'],
      key: 'current_step',
      width: 250,
      render: (_, sequence) =>
        id ? <CampaignSteps sequence={sequence} leadId={id} /> : '-'
    },
    {
      width: 50,
      fixed: 'right',
      key: 'actions',
      render: (_, sequence) => <TableActions options={actionItems(sequence)} />
    }
  ]

  const actionComponent = useMemo(() => {
    const Component = modalState.type ? modalTypes[modalState.type] : null

    return Component && modalState.sequence && id ? (
      <Component
        open
        leadId={id}
        sequence={modalState.sequence}
        onCancel={() => setModalState({ sequence: null, type: null })}
      />
    ) : null
  }, [modalState, id])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        columns={columns}
        data={data}
        loading={isLoading}
        leftChildren={<CampaignFilter />}
        rightChildren={<CampaignHeader />}
      />

      {actionComponent}
    </Flex>
  )
}
